import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

const useJobData = (jobID) => {
  const [jobData, setJobData] = useState(null);
  const [allJobData, setAllJobData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchJobData = async () => {
      if (!jobID) {
        setError('Invalid job ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Validate jobID format (basic Firestore ID validation)
        if (typeof jobID !== 'string' || jobID.length === 0) {
          throw new Error('Invalid job ID format');
        }

        // Fetch job data from Firestore
        const jobRef = doc(db, 'JobData', jobID);
        const jobSnap = await getDoc(jobRef);

        if (!jobSnap.exists()) {
          throw new Error('Job not found');
        }

        const data = {
          ...jobSnap.data(),
          id: jobSnap.id,
        };

        // Validate required fields for folder path construction
        if (!data.client || !data.jobNumber) {
          throw new Error('Job data is incomplete - missing client or job number');
        }

        setJobData(data);

        // Check if this is a multi-day job and fetch all related jobs
        const isMultiDay =
          data.isMultiDay ||
          data.parentJobId ||
          (data.multiDayChildren && data.multiDayChildren.length > 0);

        if (isMultiDay) {
          const allJobs = await fetchAllRelatedJobs(data);
          setAllJobData(allJobs);
        } else {
          setAllJobData([data]);
        }

      } catch (err) {
        console.error('Error fetching job data:', err);
        setError(err.message || 'Failed to load job data');
      } finally {
        setLoading(false);
      }
    };

    fetchJobData();
  }, [jobID]);

  // Helper function to fetch all related multi-day jobs
  const fetchAllRelatedJobs = async (currentJob) => {
    try {
      // Determine the parent job ID
      const parentId = currentJob.parentJobId || currentJob.id;

      // Fetch the parent job
      const parentRef = doc(db, 'JobData', parentId);
      const parentSnap = await getDoc(parentRef);

      if (!parentSnap.exists()) {
        console.warn('Parent job not found, returning current job only');
        return [currentJob];
      }

      const parentData = {
        ...parentSnap.data(),
        id: parentSnap.id,
      };

      // Fetch all child jobs
      const childIds = parentData.multiDayChildren || [];
      const childJobs = [];

      if (childIds.length > 0) {
        const childPromises = childIds.map(async (childId) => {
          const childRef = doc(db, 'JobData', childId);
          const childSnap = await getDoc(childRef);
          if (childSnap.exists()) {
            return {
              ...childSnap.data(),
              id: childSnap.id,
            };
          }
          return null;
        });

        const childResults = await Promise.all(childPromises);
        childJobs.push(...childResults.filter(job => job !== null));
      }

      // Combine parent and children, then sort by multiDayOrder
      const allJobs = [parentData, ...childJobs].sort(
        (a, b) => (a.multiDayOrder || 1) - (b.multiDayOrder || 1)
      );

      return allJobs;
    } catch (err) {
      console.error('Error fetching related multi-day jobs:', err);
      return [currentJob]; // Fallback to just the current job
    }
  };

  // Helper function to generate the base Dropbox path
  const getBasePath = () => {
    if (!jobData) return null;

    return `/${jobData.client}/${jobData.jobNumber}-${jobData.client}${jobData.jobTypeShort || ''}${jobData.multipleDays ? `/${jobData.multipleDays}` : ''
      }`;
  };

  return {
    jobData,
    allJobData,
    loading,
    error,
    basePath: getBasePath(),
  };
};

export default useJobData;